'use client';

import cn from 'classnames';
import { useId, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { responsiveBreakpointsPx } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { HeadlineChannel, ListenOption } from '@/packages/sxm/sports/channelWaterfall';
import { ChannelChip } from '@/ui-components/atoms/ChannelChip/ChannelChip';
import { BottomSheet } from '@/ui-components/molecules/BottomSheet/BottomSheet';
import { BroadcastGrid } from '@/ui-components/molecules/BroadcastGrid/BroadcastGrid';
import { buildBroadcastRows } from '@/ui-components/molecules/BroadcastGrid/broadcastRows';
import type { DynamicTableRow } from '@/ui-components/molecules/DynamicTable/DynamicTable';
import { overflowBelow, placePopover } from './popoverPosition';
import styles from './listenOptions.module.scss';

export interface ListenOptionsProps {
    entityName: string;
    headline: HeadlineChannel;
    options: ListenOption[];
    dataCy?: string;
    popoverAnchor?: {
        name: string;
        horizontalInset: number;
        gap: number;
    };
    popoverAnchorRef?: RefObject<HTMLElement | null>;
}

interface OptionsViewProps {
    entityName: string;
    channel: string;
    device: HeadlineChannel['device'];
    rows: DynamicTableRow[];
    dataCy?: string;
    popoverAnchor?: ListenOptionsProps['popoverAnchor'];
    popoverAnchorRef?: ListenOptionsProps['popoverAnchorRef'];
}

function getChipAriaLabel(entityName: string, channel: string) {
    return `Ways to listen to ${entityName}, channel ${channel}`;
}

function positionWithoutAnchor(
    anchor: HTMLElement | null,
    popover: HTMLElement,
    horizontalInset: number,
    gap: number,
    matchAnchorWidth: boolean,
) {
    if (!anchor) return;
    const { top, bottom, left, width } = anchor.getBoundingClientRect();
    const supportsAnchorPositioning = CSS.supports('anchor-name', '--a');
    if (matchAnchorWidth && (!supportsAnchorPositioning || !CSS.supports('width', 'anchor-size(width)'))) {
        popover.style.width = `${Math.max(0, width - 2 * horizontalInset)}px`;
    }
    if (supportsAnchorPositioning) return;
    const placement = placePopover({
        anchor: { top, bottom, left: left + horizontalInset },
        popover: { width: popover.offsetWidth, height: popover.offsetHeight },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        gap,
    });
    popover.style.top = `${placement.top}px`;
    popover.style.left = `${placement.left}px`;
}

const SCROLL_BEHAVIOR = { true: 'auto', false: 'smooth' } as const;

function revealBelow(anchor: HTMLElement | null, popover: HTMLElement, gap: number) {
    if (!anchor) return;
    const distance = overflowBelow({
        anchorBottom: anchor.getBoundingClientRect().bottom,
        popoverHeight: popover.offsetHeight,
        viewportHeight: window.innerHeight,
        gap,
    });
    if (!distance) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: distance, behavior: SCROLL_BEHAVIOR[`${reduceMotion}`] });
}

function DesktopOptions({ entityName, rows, popoverAnchor, popoverAnchorRef, ...chip }: OptionsViewProps) {
    const id = useId();
    const chipRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const label = `Ways to listen to ${entityName}`;
    const anchorName = popoverAnchor?.name ?? `--${id.replace(/[^a-zA-Z0-9-]/g, '')}`;
    const anchor = {
        '--popover-anchor': anchorName,
        '--popover-horizontal-inset': `${popoverAnchor?.horizontalInset ?? 0}px`,
        '--popover-gap': `${popoverAnchor?.gap ?? 8}px`,
        ...(popoverAnchor ? {} : { '--chip-anchor': anchorName }),
    } as CSSProperties;

    const chipLabel = getChipAriaLabel(entityName, chip.channel);

    return (
        <div ref={chipRef} className={styles.anchor} style={anchor}>
            <ChannelChip {...chip} ariaLabel={chipLabel} ariaExpanded={open} ariaControls={id} popoverTarget={id} />
            <div
                id={id}
                popover="auto"
                role="dialog"
                aria-label={label}
                className={cn(styles.popover, { [styles.sizedToAnchor]: !!popoverAnchor })}
                onToggle={(event) => {
                    const isOpen = event.newState === 'open';
                    setOpen(isOpen);
                    if (isOpen) {
                        const anchorElement =
                            popoverAnchorRef?.current ?? chipRef.current?.querySelector('button') ?? null;
                        const gap = popoverAnchor?.gap ?? 8;
                        positionWithoutAnchor(
                            anchorElement,
                            event.currentTarget,
                            popoverAnchor?.horizontalInset ?? 0,
                            gap,
                            !!popoverAnchor,
                        );
                        revealBelow(anchorElement, event.currentTarget, gap);
                    }
                }}
            >
                <BroadcastGrid rows={rows} />
            </div>
        </div>
    );
}

function MobileOptions({ entityName, rows, channel, device, dataCy }: OptionsViewProps) {
    const id = useId();
    const [open, setOpen] = useState(false);
    const chipLabel = getChipAriaLabel(entityName, channel);

    return (
        <>
            <ChannelChip
                channel={channel}
                device={device}
                dataCy={dataCy}
                ariaLabel={chipLabel}
                ariaExpanded={open}
                ariaControls={id}
                onClick={() => setOpen(true)}
            />
            {open && (
                <BottomSheet
                    id={id}
                    title="Ways to Listen"
                    closeLabel="Close ways to listen"
                    onClose={() => setOpen(false)}
                >
                    <BroadcastGrid rows={rows} />
                </BottomSheet>
            )}
        </>
    );
}

const OPTIONS_VIEWS = { true: DesktopOptions, false: MobileOptions };

export function ListenOptions({
    entityName,
    headline,
    options,
    dataCy,
    popoverAnchor,
    popoverAnchorRef,
}: ListenOptionsProps) {
    const desktop = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`);

    const Options = OPTIONS_VIEWS[`${desktop}`];

    return (
        <div className={styles.options}>
            <Options
                entityName={entityName}
                channel={`${headline.channel}`}
                device={headline.device}
                rows={buildBroadcastRows(options, entityName)}
                dataCy={dataCy}
                popoverAnchor={popoverAnchor}
                popoverAnchorRef={popoverAnchorRef}
            />
        </div>
    );
}
